import { Outlet, NavLink, Link } from 'react-router-dom'
import { useAuthStore } from '../../hooks/useAuth'
import { useState, Suspense } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../../lib/api'

function ContentLoader() {
  return (
    <div className="flex-1 flex items-center justify-center py-24">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-aqua-200 border-t-aqua-500 rounded-full animate-spin" />
        <p className="text-sm text-gray-400 font-semibold">Cargando...</p>
      </div>
    </div>
  )
}
import {
  LayoutDashboard, Package, Users, ShoppingBag, MapPin,
  Bell, Store, LogOut, Menu, X, Fish, GraduationCap, ShoppingCart, Receipt, Warehouse, Tag, ShieldCheck, FileText, Bot, Link2, Star, Factory, TrendingUp, Truck, FileSearch, CalendarClock, ClipboardList, CalendarCheck, Briefcase, Boxes, Building2, Banknote, Sparkles, KeyRound, Check, RefreshCw, BookOpen, Ticket
} from 'lucide-react'
import { fecSidebar } from '../../lib/dateUtils'
import PulpinWidget from '../ui/PulpinWidget'
import clsx from 'clsx'
import toast from 'react-hot-toast'

// ── Modal: cambiar mi contraseña ──────────────────────────────────────────────
function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [actual, setActual] = useState('')
  const [nueva, setNueva] = useState('')
  const [confirmar, setConfirmar] = useState('')
  const [saving, setSaving] = useState(false)

  async function handleSubmit() {
    if (nueva.length < 6) { toast.error('La nueva contraseña debe tener al menos 6 caracteres'); return }
    if (nueva !== confirmar) { toast.error('Las contraseñas no coinciden'); return }
    setSaving(true)
    try {
      await api.post('/auth/change-password', { actual, nueva })
      toast.success('Contraseña actualizada ✅', { duration: 4000 })
      onClose()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'No se pudo cambiar la contraseña')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="font-extrabold text-gray-900 flex items-center gap-2"><KeyRound size={18} className="text-aqua-500" /> Cambiar contraseña</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Contraseña actual</label>
            <input type="password" value={actual} onChange={e => setActual(e.target.value)} className="input-field" placeholder="Tu contraseña actual" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Nueva contraseña</label>
            <input type="password" value={nueva} onChange={e => setNueva(e.target.value)} className="input-field" placeholder="Mínimo 6 caracteres" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Confirmar nueva contraseña</label>
            <input type="password" value={confirmar} onChange={e => setConfirmar(e.target.value)} className="input-field" placeholder="Repite la nueva contraseña"
              onKeyDown={e => { if (e.key === 'Enter') handleSubmit() }} />
          </div>
        </div>
        <div className="flex gap-3 p-5 border-t border-gray-100">
          <button onClick={onClose} className="btn-secondary flex-1">Cancelar</button>
          <button onClick={handleSubmit} disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50">
            {saving ? <RefreshCw size={16} className="animate-spin" /> : <Check size={16} />} Guardar
          </button>
        </div>
      </div>
    </div>
  )
}

const navItems = [
  { label: 'Dashboard', to: '/admin', icon: LayoutDashboard, end: true },
  { label: 'Inventario', to: '/admin/inventario', icon: Package },
  { label: 'Clientes', to: '/admin/clientes', icon: Users },
  { label: 'Productos', to: '/admin/productos', icon: Fish },
  { label: 'Sedes', to: '/admin/sedes', icon: MapPin },
  { label: 'Notificaciones', to: '/admin/notificaciones', icon: Bell },
  { label: 'Mayoristas', to: '/admin/mayoristas', icon: Store },
  { label: 'Colaboradores', to: '/admin/colaboradores', icon: Users },
  { label: 'Tareas colab.', to: '/admin/colab-tareas', icon: ClipboardList },
  { label: 'Capacitación',  to: '/admin/capacitacion',  icon: GraduationCap },
  { label: 'Despachos', to: '/admin/despachos', icon: Truck },
  { label: 'Producción',   to: '/admin/produccion',    icon: Factory },
  { label: 'AquaLearn', to: '/admin/aqualearn', icon: GraduationCap },
  { label: 'Finanzas', to: '/admin/finanzas', icon: Banknote },
  { label: 'Pagos Fijos', to: '/admin/costos', icon: CalendarClock },
  { label: 'Cotizaciones', to: '/admin/cotizaciones', icon: FileSearch },
  { label: 'Márgenes', to: '/admin/margenes', icon: TrendingUp },
  { label: 'Bodega', to: '/admin/bodega', icon: Warehouse },
  { label: 'Promociones', to: '/admin/promociones', icon: Tag },
  { label: 'Rifa', to: '/admin/rifa', icon: Ticket },
  { label: 'Banners',    to: '/admin/highlights',  icon: Sparkles },
  { label: 'Libros', to: '/admin/libros', icon: BookOpen },
  { label: 'Reportes',    to: '/admin/reportes',   icon: FileText },
  { label: 'Auditoría',   to: '/admin/auditoria',  icon: ShieldCheck },
  { label: 'Aqua Puntos', to: '/admin/puntos',        icon: Star },
  { label: '🐙 Pulpín',   to: '/admin/pulpin',        icon: Bot },
  { label: '🔗 Bot Bridge', to: '/admin/pulpin-bridge', icon: Link2 },
  { label: '👥 Contactos Pulpín', to: '/admin/pulpin-contactos', icon: Users },
  { label: 'Supervisor',  to: '/admin/supervisor',  icon: ClipboardList },
  { label: 'Asistencia',  to: '/admin/asistencia',  icon: CalendarCheck },
  { label: 'Accionistas', to: '/admin/accionistas', icon: Briefcase },
  { label: 'Insumos', to: '/admin/insumos', icon: Boxes },
  { label: 'Activos Fijos', to: '/admin/activos', icon: Building2 },
  { label: '🐠 Inventario Vivo', to: '/admin/vida', icon: Fish },
]

export default function AdminLayout() {
  const { user, logout } = useAuthStore()
  const [open, setOpen] = useState(false)
  const [showPwd, setShowPwd] = useState(false)

  const { data: unreadData } = useQuery<{ count: number }>({
    queryKey: ['admin-notif-count'],
    queryFn: () => api.get('/admin/notifications/unread-count').then((r: any) => r.data),
    refetchInterval: 30_000,
  })
  const unreadCount = unreadData?.count ?? 0

  return (
    <div className="flex min-h-screen bg-aqua-50">
      {/* Sidebar */}
      <aside className={clsx(
        'fixed inset-y-0 left-0 z-50 w-60 bg-aqua-500 flex flex-col transition-transform duration-250 lg:translate-x-0',
        open ? 'translate-x-0' : '-translate-x-full'
      )}>
        {/* Logo */}
        <div className="px-5 py-5 border-b border-white/15">
          <img src="/logo-aqua.png" alt="Aqua" className="h-10 w-auto brightness-0 invert" />
          <div className="text-xs font-bold uppercase tracking-widest text-white/40 mt-0.5">Panel administrador</div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navItems.map(({ label, to, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) => clsx(
                'flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all',
                isActive
                  ? 'bg-white/20 text-white'
                  : 'text-white/65 hover:bg-white/10 hover:text-white'
              )}
              onClick={() => setOpen(false)}
            >
              <Icon size={17} />
              <span className="flex-1">{label}</span>
              {to === '/admin/notificaciones' && unreadCount > 0 && (
                <span className="ml-auto bg-red-400 text-white text-[10px] font-extrabold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User footer */}
        <div className="p-4 border-t border-white/15">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {user?.avatar}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-white truncate">{user?.nombre}</div>
              <div className="text-xs text-white/40 font-semibold">Administrador</div>
            </div>
            <button onClick={() => setShowPwd(true)} className="text-white/50 hover:text-white transition-colors p-1" title="Cambiar contraseña">
              <KeyRound size={16} />
            </button>
            <button onClick={logout} className="text-white/50 hover:text-white transition-colors p-1" title="Cerrar sesión">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}

      {/* Overlay mobile */}
      {open && <div className="fixed inset-0 z-40 bg-aqua-900/40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <main className="flex-1 lg:ml-60 flex flex-col min-h-screen">
        {/* Topbar */}
        <header className="h-15 bg-transparent flex items-center justify-between px-5 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 rounded-lg hover:bg-aqua-100" onClick={() => setOpen(true)}>
              <Menu size={20} className="text-gray-600" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className="badge badge-teal hidden sm:inline-flex">
              {fecSidebar(new Date())}
            </span>
            {/* Bell with unread badge */}
            <Link
              to="/admin/notificaciones"
              className="relative p-2 rounded-xl hover:bg-aqua-100 transition-colors text-gray-500 hover:text-aqua-600"
              title="Notificaciones"
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
          </div>
        </header>

        <div className="flex-1 p-5 md:p-6">
          <Suspense fallback={<ContentLoader />}>
            <Outlet />
          </Suspense>
        </div>
      </main>

      {/* Widget flotante Pulpín — Fase 1 */}
      <PulpinWidget />
    </div>
  )
}
